/**
 * The Pick'em part of the Settings tab (ISSUE-1306, ISSUE-1310), rendered at
 * the top of the tab by `features/poolScreen/PoolSettingsTabPane.tsx`. The
 * generic Chat, Notifications and pool settings cards stay below it.
 *
 * Every member reads the pool's rules here, as the server writes them. The
 * Commissioner also gets the one button that opens the settings dialog, and
 * the list of who still owes picks this round with a Nudge on each.
 */
import { usePickemPoolGet } from "@sportsmet/contest-sdk/private";
import { usePoolGet } from "@sportsmet/pool-sdk/private";
import { Button, Card, InlineBanner, SavedNote, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { useOverlay } from "../../routeState/useOverlay";
import type { typePoolTypePaneProps } from "../poolScreen/poolTypePaneProps";
import {
  PICKEM_RETRY,
  PICKEM_SETTINGS_DIALOG_NAME,
  PICKEM_SETTINGS_EDIT_LABEL,
  PICKEM_SETTINGS_FAILED,
  PICKEM_SETTINGS_HEADING,
  PICKEM_SETTINGS_LOADING,
  PICKEM_SETTINGS_NO_CONTEST,
  pickemPendingChangeLine,
  pickemSettingsSavedLine,
} from "./pickemCopy";
import { PickemMissingPicksCard } from "./PickemMissingPicksCard";
import { PickemRulesLines } from "./PickemRulesLines";
import { PickemSettingsDialog } from "./PickemSettingsDialog";

export const PickemSettingsSection = ({
  poolId,
}: typePoolTypePaneProps): ReactNode => {
  const query = usePickemPoolGet({ poolId });
  const data = query.data?.data ?? null;
  const pool = usePoolGet({ poolId });
  const leagueKey = pool.data?.data?.pool.leagueKey ?? "";
  const overlay = useOverlay();
  const [savedLine, setSavedLine] = useState<string | null>(null);

  let body: ReactNode;
  if (query.isLoading) {
    body = (
      <div data-testid="pickem-settings-loading">
        <Skeleton shape="card" label={PICKEM_SETTINGS_LOADING} />
      </div>
    );
  } else if (query.isError || data === null) {
    body = (
      <div data-testid="pickem-settings-failed">
        <InlineBanner
          variant="warning"
          message={PICKEM_SETTINGS_FAILED}
          action={{
            label: PICKEM_RETRY,
            onAction: () => {
              void query.refetch();
            },
          }}
        />
      </div>
    );
  } else {
    const settings = data.settings;
    const pendingLabel = settings?.pendingChangeFromRoundLabel ?? null;
    const isDialogOpen = overlay.isOpen("dialog", PICKEM_SETTINGS_DIALOG_NAME);
    const roundId = data.currentRoundId;
    body = (
      <>
        <Card data-testid="pickem-rules-card">
          <h2 className="text-body-strong text-ink">
            {PICKEM_SETTINGS_HEADING}
          </h2>
          {settings === null || data.rulesLines.length === 0 ? (
            <p className="text-body text-ink-secondary">
              {PICKEM_SETTINGS_NO_CONTEST}
            </p>
          ) : (
            <PickemRulesLines lines={data.rulesLines} />
          )}
          {pendingLabel !== null ? (
            <p
              className="text-caption text-ink-secondary"
              data-testid="pickem-pending-change"
            >
              {pickemPendingChangeLine(pendingLabel)}
            </p>
          ) : null}
          {data.viewer.canEditSettings && settings !== null ? (
            <div className="flex flex-col gap-2">
              <Button
                variant="secondary"
                className="self-start"
                data-testid="pickem-settings-edit"
                onClick={() => {
                  setSavedLine(null);
                  overlay.open({
                    kind: "dialog",
                    name: PICKEM_SETTINGS_DIALOG_NAME,
                  });
                }}
              >
                {PICKEM_SETTINGS_EDIT_LABEL}
              </Button>
              <SavedNote saved={savedLine !== null} label={savedLine ?? ""} />
            </div>
          ) : null}
        </Card>
        {data.viewer.isAdmin && roundId !== null ? (
          <PickemMissingPicksCard poolId={poolId} roundId={roundId} />
        ) : null}
        {data.viewer.canEditSettings && settings !== null ? (
          <PickemSettingsDialog
            open={isDialogOpen}
            poolId={poolId}
            leagueKey={leagueKey}
            settings={settings}
            onClose={() => {
              overlay.close({ kind: "dialog" });
            }}
            onSaved={(label) => {
              setSavedLine(pickemSettingsSavedLine(label));
              overlay.close({ kind: "dialog" });
            }}
          />
        ) : null}
      </>
    );
  }

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="pickem-settings-section"
      data-pool-id={poolId}
    >
      {body}
    </div>
  );
};
